import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User as UserIcon, ArrowRight, KeyRound, Sparkles, Star, Fingerprint, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { AppLogo } from '../components/AppLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Big Spinning Logo for Loading Screen
const LoadingLogo = () => (
  <motion.div
    className="flex flex-col items-center justify-center"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
  >
    <motion.img
      src="/logo-mark.png"
      alt="EasyPassing"
      className="w-28 h-28 md:w-32 md:h-32 rounded-3xl object-cover shadow-2xl shadow-[#2B4C7E]/40"
      animate={{ scale: [1, 1.06, 1] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
    />
    
    <motion.p 
      className="mt-6 text-white text-xl font-medium"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    >
      Signing you in...
    </motion.p>
  </motion.div>
);

// Enhanced Sunset background with more visual elements
const SunsetBackground = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Animated gradient overlay */}
    <motion.div 
      className="absolute inset-0"
      animate={{
        background: [
          'linear-gradient(to bottom, #1E3A63, #2B4C7E, #D0B384, #F7F1E8)',
          'linear-gradient(to bottom, #2B4C7E, #3A5C8F, #C8A97E, #F2E9D9)',
          'linear-gradient(to bottom, #1E3A63, #2B4C7E, #D0B384, #F7F1E8)',
        ]
      }}
      transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
    />
    
    {/* Sun with pulsing glow */}
    <div className="absolute top-16 left-1/2 -translate-x-1/2">
      <motion.div 
        className="w-36 h-36 rounded-full bg-gradient-to-b from-yellow-200 via-[#D0B384] to-[#C8A97E]"
        animate={{ scale: [1, 1.05, 1], opacity: [0.9, 1, 0.9] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div 
        className="absolute inset-4 rounded-full bg-gradient-to-b from-yellow-100 to-[#DCC39A]"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </div>
    
    {/* Animated sun rays */}
    <div className="absolute top-12 left-1/2 -translate-x-1/2 w-52 h-52">
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2 w-1 h-28 bg-gradient-to-t from-[#D0B384]/0 to-yellow-200/50 origin-bottom"
          style={{ transform: `translateX(-50%) rotate(${i * 30}deg)` }}
          animate={{ opacity: [0.3, 0.6, 0.3], scaleY: [1, 1.1, 1] }}
          transition={{ duration: 2, delay: i * 0.1, repeat: Infinity }}
        />
      ))}
    </div>
    
    {/* Floating clouds */}
    <motion.div 
      className="absolute top-24 left-5 w-44 h-14 bg-white/40 rounded-full blur-xl"
      animate={{ x: [0, 30, 0], opacity: [0.4, 0.6, 0.4] }}
      transition={{ duration: 8, repeat: Infinity }}
    />
    <motion.div 
      className="absolute top-16 left-20 w-36 h-12 bg-white/30 rounded-full blur-lg"
      animate={{ x: [0, -20, 0], opacity: [0.3, 0.5, 0.3] }}
      transition={{ duration: 6, repeat: Infinity }}
    />
    <motion.div 
      className="absolute top-28 right-10 w-52 h-16 bg-[#F2E9D9]/40 rounded-full blur-xl"
      animate={{ x: [0, -25, 0], opacity: [0.4, 0.5, 0.4] }}
      transition={{ duration: 10, repeat: Infinity }}
    />
    <motion.div 
      className="absolute top-20 right-28 w-40 h-12 bg-[#F2E9D9]/30 rounded-full blur-lg"
      animate={{ x: [0, 15, 0] }}
      transition={{ duration: 7, repeat: Infinity }}
    />
    
    {/* Mountains silhouette */}
    <svg className="absolute bottom-0 left-0 w-full h-72" viewBox="0 0 1920 288" preserveAspectRatio="none">
      <defs>
        <linearGradient id="mountain-back" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1E3A63" />
          <stop offset="100%" stopColor="#0F1E36" />
        </linearGradient>
        <linearGradient id="mountain-front" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2B4C7E" />
          <stop offset="100%" stopColor="#182E4F" />
        </linearGradient>
      </defs>
      {/* Back mountains */}
      <polygon points="0,288 0,170 200,90 400,180 600,70 800,160 1000,60 1200,150 1400,80 1600,170 1920,100 1920,288" fill="url(#mountain-back)" opacity="0.7" />
      {/* Front mountains */}
      <polygon points="0,288 0,200 150,140 350,200 550,120 750,190 950,110 1150,180 1350,120 1550,200 1750,130 1920,190 1920,288" fill="url(#mountain-front)" />
      {/* Snow caps with glow */}
      <polygon points="550,120 515,155 585,155" fill="#F7F1E8" opacity="0.9" />
      <polygon points="950,110 910,150 990,150" fill="#F7F1E8" opacity="0.9" />
      <polygon points="1350,120 1310,160 1390,160" fill="#F7F1E8" opacity="0.9" />
    </svg>
    
    {/* Twinkling stars */}
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1.5 h-1.5 bg-white rounded-full"
        style={{
          top: `${5 + Math.random() * 25}%`,
          left: `${Math.random() * 100}%`,
        }}
        animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.3, 1] }}
        transition={{ duration: 2 + Math.random() * 2, delay: Math.random() * 2, repeat: Infinity }}
      />
    ))}
  </div>
);

export default function Login() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(!(new URLSearchParams(window.location.search).get('mode') === 'signup'));
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [referralCode, setReferralCode] = useState(
    (new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('referral_code') || '').toUpperCase()
  );
  
  // 2FA State
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  
  // Biometric login state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [checkingBiometric, setCheckingBiometric] = useState(false);

  // Check if biometric is available when email changes
  useEffect(() => {
    const checkBiometric = async () => {
      if (!formData.email || formData.email.length < 5) {
        setBiometricAvailable(false);
        return;
      }
      
      // Check if WebAuthn is available
      if (!window.PublicKeyCredential) {
        setBiometricAvailable(false);
        return;
      }
      
      try {
        const platformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (!platformAvailable) {
          setBiometricAvailable(false);
          return;
        }
        
        // Check if user has biometric login enabled
        const response = await axios.get(`${BACKEND_URL}/api/auth/biometric/check/${encodeURIComponent(formData.email)}`);
        setBiometricAvailable(response.data.biometric_available);
      } catch (error) {
        setBiometricAvailable(false);
      }
    };
    
    const timeout = setTimeout(checkBiometric, 500);
    return () => clearTimeout(timeout);
  }, [formData.email]);

  // After successful auth, honor a pending membership plan chosen from the pricing page.
  const finishAuth = () => {
    const pendingPlan = localStorage.getItem('pending_plan');
    if (pendingPlan) {
      localStorage.removeItem('pending_plan');
      navigate(`/membership?plan=${pendingPlan}`);
    } else {
      navigate('/dashboard');
    }
  };


  const handleBiometricLogin = async () => {
    setCheckingBiometric(true);
    
    try {
      // Create WebAuthn challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      
      // Request biometric authentication
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: 60000,
          userVerification: 'required',
          rpId: window.location.hostname,
        }
      });
      
      if (credential) {
        // Login with biometric
        const response = await axios.post(`${BACKEND_URL}/api/auth/biometric/login?credential_id=${credential.id}`);
        
        const { token, user } = response.data;
        localStorage.setItem('session_token', token);
        toast.success(`Welcome back, ${user.name}!`);
        finishAuth();
      }
    } catch (error) {
      console.error('Biometric login failed:', error);
      toast.error('Biometric login failed. Please use email/password.');
    } finally {
      setCheckingBiometric(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        // Check if 2FA is required
        if (response.data.requires_2fa) {
          setTwoFactorEmail(formData.email);
          setShow2FA(true);
          toast.info('Please enter the verification code sent to your email');
          setLoading(false);
          return;
        }
        
        const { token, user } = response.data;
        localStorage.setItem('session_token', token);
        toast.success(`Welcome back, ${user.name}!`);
        finishAuth();
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          referral_code: referralCode || null
        });
        
        const { token, user } = response.data;
        localStorage.setItem('session_token', token);
        toast.success(`Welcome, ${user.name}!`);
        finishAuth();
      }
    } catch (error) {
      console.error('Auth error:', error);
      const detail = error.response?.data?.detail || 'Authentication failed';
      // If a logged-out user tried to sign up with an existing email (e.g., arriving from a
      // pricing CTA), gracefully switch them to sign-in so they can continue to their plan.
      if (!isLogin && /already|exist|registered/i.test(String(detail))) {
        setIsLogin(true);
        toast.info('You already have an account — please sign in to continue.');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setVerifying2FA(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/2fa/verify?email=${encodeURIComponent(twoFactorEmail)}`,
        { code: twoFactorCode }
      );
      
      if (response.data.verified) {
        const { token, user } = response.data;
        localStorage.setItem('session_token', token);
        toast.success(`Welcome back, ${user.name}!`);
        finishAuth();
      }
    } catch (error) {
      console.error('2FA verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to dashboard - session_id will be in URL hash
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResetSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { email: resetEmail });
      toast.success('Password reset link sent to your email!');
      setResetDialogOpen(false);
      setResetEmail('');
    } catch (error) {
      // For demo, show success anyway since email might not be configured
      toast.success('If an account exists with this email, a reset link will be sent.');
      setResetDialogOpen(false);
      setResetEmail('');
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Sunset Background */}
      <SunsetBackground />
      
      {/* Loading Overlay with Big Spinning Logo */}
      <AnimatePresence>
        {loading && (
          <motion.div
            className="absolute inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#0F1E36]/90 via-[#6B5637]/90 to-[#0F1E36]/90 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingLogo />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Logo at top - icon only, no text */}
      <motion.div 
        className="absolute top-6 left-6 z-20"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <AppLogo size="lg" showText={false} />
      </motion.div>
      
      {/* Floating decorative elements */}
      <motion.div
        className="absolute top-20 right-10 z-10"
        animate={{ y: [0, -10, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <Sparkles className="h-8 w-8 text-yellow-300/60" />
      </motion.div>
      <motion.div
        className="absolute bottom-32 left-10 z-10"
        animate={{ y: [0, 10, 0], rotate: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity }}
      >
        <Star className="h-6 w-6 text-[#DCC39A]/60" />
      </motion.div>
      
      <motion.div
        className="w-full max-w-md px-4 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="relative z-10 p-8 w-full bg-white/95 backdrop-blur-md border-2 border-[#D7E0EA] shadow-2xl rounded-3xl">
          {/* Decorative corner elements */}
          <div className="absolute -top-3 -left-3 w-6 h-6 bg-gradient-to-br from-[#3A5C8F] to-[#D0B384] rounded-full" />
          <div className="absolute -top-3 -right-3 w-6 h-6 bg-gradient-to-br from-[#D0B384] to-[#D0B384] rounded-full" />
          <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-gradient-to-br from-[#3A5C8F] to-[#3A5C8F] rounded-full" />
          <div className="absolute -bottom-3 -right-3 w-6 h-6 bg-gradient-to-br from-[#3A5C8F] to-[#3A5C8F] rounded-full" />
          
          {/* Header */}
          <div className="text-center mb-8">
            <motion.h1 
              className="text-3xl font-bold bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] bg-clip-text text-transparent mb-2"
              animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
              transition={{ duration: 5, repeat: Infinity }}
            >
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </motion.h1>
            <p className="text-gray-600">
              {isLogin ? 'Sign in to continue your legacy' : 'Start preserving your memories today'}
            </p>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A5C8F]" />
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 rounded-xl border-2 border-[#EBF0F5] focus:border-[#3A5C8F] transition-colors"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </motion.div>
            )}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ambassador / Referral Code <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="e.g. JOHN25"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="rounded-xl border-2 border-[#EBF0F5] focus:border-[#3A5C8F] transition-colors uppercase tracking-wide"
                    data-testid="register-referral-input"
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A5C8F]" />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 rounded-xl border-2 border-[#EBF0F5] focus:border-[#3A5C8F] transition-colors"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setResetDialogOpen(true)}
                    className="text-xs text-[#2B4C7E] hover:text-[#1E3A63] font-medium hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A5C8F]" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 rounded-xl border-2 border-[#EBF0F5] focus:border-[#3A5C8F] transition-colors"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] text-white rounded-full hover:from-[#1E3A63] hover:via-[#9C7F55] hover:to-[#B3956B] py-6 shadow-lg shadow-[#2B4C7E]/30"
                data-testid="submit-btn"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>

            {/* Biometric Login Option */}
            {isLogin && biometricAvailable && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-3"
              >
                <Button
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={checkingBiometric}
                  variant="outline"
                  className="w-full rounded-full py-5 border-2 border-[#D7E0EA] hover:bg-[#EBF0F5] hover:border-[#B9C7D6] transition-all group"
                >
                  <Fingerprint className="h-5 w-5 mr-2 text-[#2B4C7E] group-hover:text-[#2B4C7E]" />
                  {checkingBiometric ? 'Verifying...' : 'Sign in with Face ID / Touch ID'}
                </Button>
              </motion.div>
            )}
          </form>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-[#EBF0F5]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          {/* Google Sign In */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              type="button"
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full rounded-full py-6 border-2 border-[#D7E0EA] hover:bg-[#EBF0F5] hover:border-[#B9C7D6] transition-all"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </motion.div>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-[#2B4C7E] hover:text-[#1E3A63] font-medium"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </Card>
      </motion.div>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] rounded-xl flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-white" />
              </div>
              Reset Password
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A5C8F]" />
              <Input
                type="email"
                placeholder="you@example.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="pl-10 rounded-xl border-2 border-[#EBF0F5] focus:border-[#3A5C8F]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setResetDialogOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordReset}
              disabled={resetSending}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#B3956B]"
            >
              {resetSending ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA Verification Dialog */}
      <Dialog open={show2FA} onOpenChange={(open) => {
        if (!open) {
          setShow2FA(false);
          setTwoFactorCode('');
        }
      }}>
        <DialogContent className="max-w-sm bg-gradient-to-br from-slate-900 via-[#0F1E36] to-slate-900 border-2 border-[#2B4C7E]/30 rounded-3xl text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl text-white">
              <motion.div 
                className="w-12 h-12 bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] rounded-2xl flex items-center justify-center"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Shield className="h-6 w-6" />
              </motion.div>
              <span className="bg-gradient-to-r from-[#3A5C8F] to-[#D0B384] bg-clip-text text-transparent font-bold">
                Two-Step Verification
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            <p className="text-gray-300 text-sm text-center">
              We sent a 6-digit code to <span className="text-[#3A5C8F] font-medium">{twoFactorEmail}</span>
            </p>
            
            {/* Code Input */}
            <div className="flex justify-center">
              <Input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-3xl tracking-[0.5em] w-48 bg-white/10 border-[#2B4C7E]/30 text-white placeholder:text-gray-500 rounded-xl h-16"
                maxLength={6}
                inputMode="numeric"
              />
            </div>
            
            {/* Code dots indicator */}
            <div className="flex justify-center gap-2">
              {[...Array(6)].map((_, i) => (
                <div 
                  key={i}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    twoFactorCode.length > i ? 'bg-[#2B4C7E]' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
            
            <Button
              onClick={handle2FAVerify}
              disabled={verifying2FA || twoFactorCode.length !== 6}
              className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full py-6 hover:opacity-90"
            >
              {verifying2FA ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                  <Shield className="h-5 w-5" />
                </motion.div>
              ) : (
                <>
                  <Shield className="h-5 w-5 mr-2" />
                  Verify & Sign In
                </>
              )}
            </Button>
            
            <p className="text-center text-gray-400 text-xs">
              Didn&apos;t receive the code?{' '}
              <button 
                onClick={() => {
                  // Resend code
                  axios.post(`${BACKEND_URL}/api/auth/2fa/send-code?email=${encodeURIComponent(twoFactorEmail)}`);
                  toast.success('Verification code resent');
                }}
                className="text-[#3A5C8F] hover:text-[#B9C7D6]"
              >
                Resend
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
